import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@clerk/react";
import { useUserStore } from "../../stores/userStore";
import { getEffectiveTier } from "../../lib/tier";
import { Spinner } from "../Spinner";
import type { ReactNode } from "react";

interface RequireCosmicProps {
  /** Identifier passed as ?from=<feature> on the redirect, drives PaywallPage headline. */
  feature: string;
  children: ReactNode;
}

export function RequireCosmic({ feature, children }: RequireCosmicProps) {
  const { isLoaded, isSignedIn } = useAuth();
  const profile = useUserStore((s) => s.profile);
  const location = useLocation();

  // Don't decide the tier until we actually know it. On a hard load / refresh of
  // a gated route, a signed-in paid member starts with profile === null while
  // fetchProfile is in flight; redirecting here would bounce them to the paywall
  // before their membership is even read. Mirror HomePage's `profileReady`.
  const profileReady = isLoaded && (!isSignedIn || profile !== null);
  if (!profileReady) {
    return (
      <div
        role="status"
        aria-label="Loading your membership"
        className="flex min-h-[40vh] items-center justify-center text-gold-light"
      >
        <Spinner size="lg" />
      </div>
    );
  }

  // No profile at this point means signed-out → treat as free.
  const tier = profile
    ? getEffectiveTier({
        role: profile.role,
        membershipTier: profile.membershipTier,
        membershipExpiresAt: profile.membershipExpiresAt,
        tierPreview: profile.tierPreview,
      })
    : "free";

  if (tier !== "paid") {
    return (
      <Navigate
        to={`/upgrade?from=${encodeURIComponent(feature)}`}
        replace
        state={{ returnTo: location.pathname }}
      />
    );
  }
  return <>{children}</>;
}
